import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import SearchField from 'src/shared/components/form/searchField';
import { useCallback, useMemo, useState } from 'react';
import useDebounce from 'src/shared/hooks/useDebounce';
import { createFamilyConnections } from 'src/shared/services/familyService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { toast } from 'react-toastify';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { Add, Clear, PersonAdd } from '@mui/icons-material';
import { MemberSearchFormState } from '../models/memberSearchFormState';
import { MemberFamilyMemberDataGrid } from './memberFamilyMemberDataGrid';
import { FamilyConnectionRequest } from '../models/familyMember';
import { MemberFamilyCreateNewModal } from './memberFamilyCreateNewModal';

type MemberFamilyCreateCouplingModalProps = {
  modalOpen: boolean;
  onClose: () => void;
  refetchMemberCallback: () => void;
  memberAssociationId: number;
  memberId: number;
  mainMemberLocalAssId: number;
};

export const MemberFamilyCreateCouplingModal = (props: MemberFamilyCreateCouplingModalProps) => {
  const { modalOpen, onClose, memberAssociationId, memberId, refetchMemberCallback, mainMemberLocalAssId } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [, setLoading] = useState(false);
  const [openCreateNewModal, setOpenCreateNewModal] = useState(false);
  const [searchString, setSearchString] = useState<string>('');
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);

  const debouncedSearchString = useDebounce(searchString, 300);

  const searchFilter: MemberSearchFormState = useMemo(() => {
    return {
      associationId: mainMemberLocalAssId,
      associationIds: memberAssociationId,
      districtIds: [],
      searchString: debouncedSearchString,
    } as unknown as MemberSearchFormState;
  }, [debouncedSearchString, mainMemberLocalAssId, memberAssociationId]);

  const loadingCallback = useCallback((isLoading: boolean) => {
    setLoading(isLoading);
  }, []);

  const onCloseHandler = () => {
    onClose();
  };
  const onCreateConnectionHandler = async () => {
    // plocka ut idn som ska läggas till
    const numericSelection: number[] = selectionModel.map((id) => Number(id));
    setLoading(true);

    const dataToPut: FamilyConnectionRequest[] = numericSelection.map((id) => {
      const item: FamilyConnectionRequest = {
        member_id: memberId,
        familymember_id: id,
      };

      return item;
    });

    try {
      await createFamilyConnections(dataToPut);
      if (isMounted()) {
        toast.success(<>{t('member:familyConnectionAddedSuccess')}</>);
        refetchMemberCallback();
      }
    } catch (error) {
      if (isMounted()) ConsoleHelper.log(error);
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  return (
    <>
      <GenericDialog
        open={modalOpen}
        onClose={onCloseHandler}
        onSave={onCloseHandler}
        padding={0}
        dialogTitle={t('member:addHouseholdCoupling')}
        dialogContent={
          <Box display='flex' flexDirection='column'>
            <Box display='flex' flexDirection='column' lineHeight={1.2} sx={{ p: 2 }}>
              <Typography mb={2}>{`${t('member:chooseOrCreateMember')}`}</Typography>
              <Box width='70%'>
                <SearchField onSearch={(search) => setSearchString(search)} />
              </Box>
            </Box>
            <MemberFamilyMemberDataGrid
              memberId={memberId}
              searchFilter={searchFilter}
              checkboxSelection
              loadingCallback={loadingCallback}
              pageSize={10}
              setSelectionModel={setSelectionModel}
              selectionModel={selectionModel}
              mainMemberLocalAssId={mainMemberLocalAssId}
            />
          </Box>
        }
        dialogActions={
          <Box display='flex' flexDirection='row' justifyContent='space-between' width='100%'>
            <Button variant='outlined' color='primary' disableElevation onClick={onClose} sx={{ mr: 1 }}>
              {t('common:button.cancel')}
            </Button>

            {selectionModel && selectionModel.length > 0 ? (
              <Box sx={{ display: 'flex' }}>
                <IconButton onClick={() => setSelectionModel([])} sx={{ pr: 1 }}>
                  <Clear fontSize='small' />
                </IconButton>
                <Typography variant='h6' sx={{ fontWeight: 400 }}>
                  {t('common:statusMessages.footerSelectedCount', { count: selectionModel.length })}
                </Typography>
                <Divider orientation='vertical' flexItem sx={{ mx: 1.5 }} />
                <Button variant='contained' disableElevation startIcon={<Add fontSize='small' />} onClick={onCreateConnectionHandler}>
                  {t('member:button.addCoupling')}
                </Button>
              </Box>
            ) : (
              <Button
                sx={{ mr: 2 }}
                autoFocus
                variant='contained'
                color='secondary'
                startIcon={<PersonAdd />}
                disableElevation
                onClick={() => setOpenCreateNewModal(true)}
              >
                {t('member:button.createNew')}
              </Button>
            )}
          </Box>
        }
      />
      {memberId && (
        <MemberFamilyCreateNewModal
          modalOpen={openCreateNewModal}
          onClose={() => setOpenCreateNewModal(false)}
          mainMemberId={memberId}
          onSave={() => ConsoleHelper.log('spara')}
          callback={() => refetchMemberCallback()}
        />
      )}
    </>
  );
};
